import { useState } from 'react';

import { Input } from 'components/Input';

import { fromDraft, toDraft, type DurationDraft } from './duration';

type Unit = { key: keyof DurationDraft; suffix: string };
const units: Unit[] = [
  { key: 'days', suffix: '日' },
  { key: 'hours', suffix: '時間' },
  { key: 'minutes', suffix: '分' },
];

type Props = {
  /** 何番目のステップか。読み上げ名が他のステップと重ならないようにするために使う。 */
  step: number;
  /** 「登録してから」など、待ち時間の起点を表す見出し。入力欄の前に置く。 */
  legend: string;
  /** 「後に配信する」など、入力欄の後ろに置く文字。設定内容を一文として読ませる。 */
  trailing?: string;
  /** 見出しを画面に出すか。選択肢の見出しと同じ語になる場合はfalseにして重複を避ける。 */
  showLegend?: boolean;
  value: number | null;
  onChange: (minutes: number | null) => void;
};

/**
 * 待ち時間を「◯日 ◯時間 ◯分」で入力し、内部表現の分へ正規化する。
 * 3欄すべてを空にすると未設定（null）を送る。
 * 起点と語尾を前後へ並べ、「登録してから ◯日 ◯時間 ◯分 後に配信する」と読める形にする。
 */
export function DurationField({ step, legend, trailing, showLegend = true, value, onChange }: Props) {
  const [draft, setDraft] = useState(() => toDraft(value));
  const [lastValue, setLastValue] = useState(value);
  // 外側で値が変わったとき（時刻方式の切り替え・保存後の再読み込み）だけ入力欄へ反映する。
  // pushでlastValueを先に進めるので、自分の入力が書き戻されることはない。
  if (value !== lastValue) {
    setLastValue(value);
    setDraft(toDraft(value));
  }
  const push = (next: DurationDraft) => {
    const minutes = fromDraft(next);
    setDraft(next);
    setLastValue(minutes);
    onChange(minutes);
  };
  // 「90分」のような繰り上がる入力を、入力を終えた時点で日・時間へ整える。
  // 未設定（空欄・不正値）はそのまま残し、打ち込んだ内容を黙って消さない。
  const normalize = () => {
    const minutes = fromDraft(draft);
    if (minutes !== null) setDraft(toDraft(minutes));
  };

  return (
    <fieldset className="grid gap-2">
      <legend className="sr-only">{legend}</legend>
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
        {showLegend && <span aria-hidden className="text-xs text-muted">{legend}</span>}
        {units.map(({ key, suffix }) => (
          <span key={key} className="flex items-center gap-0.5">
            <Input
              className="w-14"
              type="number"
              inputMode="numeric"
              min={0}
              aria-label={`ステップ${step} ${legend}（${suffix}）`}
              value={draft[key]}
              onChange={(event) => push({ ...draft, [key]: event.target.value })}
              onBlur={normalize}
            />
            <span className="text-xs text-muted">{suffix}</span>
          </span>
        ))}
        {trailing && <span aria-hidden className="text-xs text-muted">{trailing}</span>}
      </div>
    </fieldset>
  );
}
